import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * useIdleLogout — signs the user out after N minutes of no interaction.
 *
 * Exempt routes: any pathname ending in `/fullscreen` (the ops-center
 * TV dashboards are designed to run unattended for hours at a time, so
 * we never idle-kill their sessions).
 *
 * Activity events: mousemove, mousedown, keydown, touchstart, wheel,
 * scroll — deliberately kept to input-driven signals so a page that
 * merely refreshes data via polling doesn't count as "user is here".
 *
 * The `warnBeforeMs` window surfaces a soft toast asking the user to
 * confirm they're still around; if they interact within that window
 * the timer resets. If not, `onLogout()` fires when the full timeout
 * elapses.
 *
 * Timers are backed by `Date.now()` rather than counters, so a tab that
 * was backgrounded and throttled by the browser still enforces the
 * correct wall-clock window on wake-up.
 */
export function useIdleLogout({
  timeoutMs = 3 * 60 * 60 * 1000, // 3 hours
  warnBeforeMs = 60 * 1000,        // warn 60s before logout
  onLogout,
  onWarn,           // fires once when we enter the warn window
  onActivity,       // fires every time we reset (useful for dismissing the toast)
  enabled = true,
} = {}) {
  const location = useLocation();
  const lastActivityRef = useRef(Date.now());
  const warnedRef = useRef(false);
  // Latest callbacks kept in refs so we can install the DOM listeners /
  // interval exactly once — swapping in fresh callbacks each render
  // without churning the effect cleanup.
  const onLogoutRef = useRef(onLogout);
  const onWarnRef = useRef(onWarn);
  const onActivityRef = useRef(onActivity);
  useEffect(() => { onLogoutRef.current = onLogout; }, [onLogout]);
  useEffect(() => { onWarnRef.current = onWarn; }, [onWarn]);
  useEffect(() => { onActivityRef.current = onActivity; }, [onActivity]);

  // Fullscreen ops-center routes are opt-out. Check by pathname suffix
  // — Route rendered inside <BareAuth> so this hook may not even be
  // mounted there, but this belt-and-suspenders keeps behaviour correct
  // if the hook ever moves higher in the tree.
  const isFullscreenRoute = location.pathname.endsWith("/fullscreen");
  const active = enabled && !isFullscreenRoute;

  useEffect(() => {
    if (!active) return undefined;
    // Fresh mount / route change = fresh activity window. Prevents an
    // "already idle" hangover from stale state when navigating between
    // pages after a long pause.
    lastActivityRef.current = Date.now();
    warnedRef.current = false;

    const bump = () => {
      lastActivityRef.current = Date.now();
      if (warnedRef.current) {
        warnedRef.current = false;
        if (onActivityRef.current) onActivityRef.current();
      }
    };
    const events = ["mousemove", "mousedown", "keydown", "touchstart", "wheel", "scroll"];
    events.forEach((e) => window.addEventListener(e, bump, { passive: true }));

    const tick = setInterval(() => {
      const idle = Date.now() - lastActivityRef.current;
      if (idle >= timeoutMs) {
        if (onLogoutRef.current) onLogoutRef.current();
      } else if (idle >= timeoutMs - warnBeforeMs && !warnedRef.current) {
        warnedRef.current = true;
        if (onWarnRef.current) onWarnRef.current();
      }
    }, 15 * 1000); // 15s cadence — accurate enough for a 3h window, cheap

    return () => {
      events.forEach((e) => window.removeEventListener(e, bump));
      clearInterval(tick);
    };
  }, [active, timeoutMs, warnBeforeMs]);
}
